/**
 * Sub-contacts: other people at a marketing contact's group besides the
 * primary contact (who stays in contact_name / contact_email / phone1...).
 * Stored by demoslides as contact.sub_contacts; see normalize_sub_contacts
 * there for the server-side rules.
 */

export const newSubContactId = () =>
  Array.from({ length: 12 }, () =>
    Math.floor(Math.random() * 16).toString(16)
  ).join("");

export const emptySubContact = () => ({
  id: newSubContactId(),
  name: "",
  title: "",
  email: "",
  phone1: "",
  phone1_type: "",
  phone2: "",
  phone2_type: "",
  date_sent: "",
  unsubscribed: false,
  unsubscribed_reason: "",
  unsubscribed_date: "",
});

export const getSubContacts = (contact) =>
  Array.isArray(contact && contact.sub_contacts) ? contact.sub_contacts : [];

export const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email || "");

/** One line per person, matching the internal emails' format:
 *  "Tom Lee (Associate Pastor) · tom@x.org · (206) 555-0100 (Mobile)". */
export const formatSubContact = (sub) => {
  const name = (sub.name || "").trim();
  const title = (sub.title || "").trim();
  const who = name && title ? `${name} (${title})` : name || title;
  const parts = [who, (sub.email || "").trim()];
  [1, 2].forEach((n) => {
    const phone = (sub[`phone${n}`] || "").trim();
    const kind = (sub[`phone${n}_type`] || "").trim();
    if (phone) parts.push(kind ? `${phone} (${kind})` : phone);
  });
  const line = parts.filter(Boolean).join(" · ");
  return sub.unsubscribed ? `${line} [unsubscribed]` : line;
};

/** Text the table search should match for a contact's sub-contacts. */
export const subContactSearchFields = (contact) =>
  getSubContacts(contact).flatMap((s) => [s.name, s.title, s.email]);

/**
 * Swap the primary contact with one of the sub-contacts. Person-level
 * fields move with the person (name, title, email, phones, unsubscribe
 * state). The group's send history -- email_sent, date_sent and delivery
 * tracking -- stays with the group; the old primary carries the group's
 * date_sent as their own, since that is the last time they were emailed.
 */
export const swapPrimaryWithSubContact = (contact, subId) => {
  const subs = getSubContacts(contact);
  const index = subs.findIndex((s) => s.id === subId);
  if (index === -1) return contact;
  const sub = subs[index];

  const oldPrimary = {
    id: newSubContactId(),
    name: contact.contact_name || "",
    title: contact.contact_title || "",
    email: contact.contact_email || "",
    phone1: contact.phone1 || "",
    phone1_type: contact.phone1_type || "",
    phone2: contact.phone2 || "",
    phone2_type: contact.phone2_type || "",
    date_sent: contact.date_sent || "",
    unsubscribed: !!contact.unsubscribed,
    unsubscribed_reason: contact.unsubscribed_reason || "",
    unsubscribed_date: contact.unsubscribed_date || "",
  };

  const nextSubs = [...subs];
  nextSubs[index] = oldPrimary;

  return {
    ...contact,
    contact_name: sub.name || "",
    contact_title: sub.title || "",
    contact_email: sub.email || "",
    phone1: sub.phone1 || "",
    phone1_type: sub.phone1_type || "",
    phone2: sub.phone2 || "",
    phone2_type: sub.phone2_type || "",
    unsubscribed: !!sub.unsubscribed,
    unsubscribed_reason: sub.unsubscribed_reason || "",
    unsubscribed_date: sub.unsubscribed_date || "",
    sub_contacts: nextSubs,
  };
};
